import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import {
  DRIVER_APP_NAME,
  DRIVER_ROUTES,
  DRIVER_SUPPORT,
} from '../data/driverAppSupport';
import '../styles/DriverLegal.css';

const DriverTerms = () => {
  const lastUpdated = '4 October 2026';

  return (
    <main className="driver-legal-page">
      <SeoHead
        title="EasyDropTaxi Driver App Terms & Conditions"
        description="Terms and Conditions for authorized EasyDropTaxi Driver App users, covering account use, trip workflow, odometer accuracy, and prohibited activities."
        path={DRIVER_ROUTES.terms}
      />

      <section className="page-banner block-space text-white" style={{ backgroundColor: 'var(--secondary-color)' }}>
        <div className="container">
          <h1 className="display-4 fw-bold">Driver Terms &amp; Conditions</h1>
          <p className="lead mb-0">
            Terms of use for authorized EasyDropTaxi drivers using the {DRIVER_APP_NAME} App.
          </p>
        </div>
      </section>

      <section className="block-space">
        <div className="container">
          <div className="driver-legal-content">
            <p>
              These Terms &amp; Conditions (&quot;Terms&quot;) govern use of the {DRIVER_APP_NAME}
              mobile application (the &quot;App&quot;) by authorized EasyDropTaxi drivers. By accessing
              or using the App, you agree to follow these Terms.
            </p>
            <p><strong>Last updated:</strong> {lastUpdated}</p>

            <h2>Purpose</h2>
            <p>
              The {DRIVER_APP_NAME} App is an operational application provided for authorized
              EasyDropTaxi drivers to manage assigned trips and related trip duties.
            </p>

            <h2>Authorized Use</h2>
            <p>Only authorized EasyDropTaxi drivers may use the App.</p>
            <p>Drivers must not:</p>
            <ul>
              <li>Share login credentials with any other person</li>
              <li>Allow unauthorized persons to use their account</li>
              <li>Access another driver&apos;s account</li>
              <li>Misuse the application for any unauthorized purpose</li>
            </ul>

            <h2>Booking &amp; Customer Information</h2>
            <p>
              Drivers may receive booking and customer information required to complete assigned
              trips. Customer information must be used only for legitimate trip-related purposes
              and must not be copied, shared, sold, or disclosed without authorization.
            </p>

            <h2>Trip Workflow</h2>
            <p>Drivers are expected to follow the App trip workflow where applicable:</p>
            <p>
              Assigned Trip → Start Trip → Start KM / Opening Odometer Photo → Trip →
              End Trip → Closing KM / Closing Odometer Photo → Customer Collection /
              Required Trip Details
            </p>

            <h2>Odometer Information</h2>
            <p>
              Drivers must submit genuine and accurate odometer information and photographs.
              The following are prohibited:
            </p>
            <ul>
              <li>False KM readings</li>
              <li>Edited or fake photographs</li>
              <li>Another vehicle&apos;s odometer photograph</li>
              <li>Unrelated photographs submitted in place of required trip photos</li>
            </ul>

            <h2>Customer Collection / Payment</h2>
            <p>
              Where the App requires customer collection or payment details, the driver must enter
              the actual amount collected. False, incomplete, or misleading payment information is
              prohibited.
            </p>

            <h2>Safe Driving</h2>
            <p>
              Drivers must follow applicable traffic laws and must not use a phone or the App in a
              manner that compromises road safety. Trip updates and photograph capture should be
              completed only when it is safe and lawful to do so.
            </p>

            <h2>Prohibited Activities</h2>
            <p>Drivers must not engage in:</p>
            <ul>
              <li>Unauthorized access to the App or related systems</li>
              <li>Submitting fraudulent trip information</li>
              <li>Customer data misuse</li>
              <li>App tampering, reverse engineering, or unauthorized modification</li>
              <li>Malicious activity of any kind</li>
              <li>Unauthorized commercial use of the App or EasyDropTaxi materials</li>
              <li>Sharing confidential booking information outside authorized operations</li>
            </ul>

            <h2>Suspension / Termination</h2>
            <p>
              EasyDropTaxi may suspend or terminate access to the App where reasonably necessary,
              including for security violations, misuse, fraud, unauthorized access, or any
              violation of these Terms.
            </p>

            <h2>Privacy</h2>
            <p>
              Use of the App is also subject to the Driver Privacy Policy:
            </p>
            <p>
              <Link to={DRIVER_ROUTES.privacy}>EasyDropTaxi Driver App Privacy Policy</Link>
            </p>

            <h2>Contact</h2>
            <p>For questions about these Terms, contact EasyDropTaxi support:</p>
            <div className="driver-contact-panel">
              <p>
                <strong>Phone:</strong>{' '}
                <a href={DRIVER_SUPPORT.phoneTel}>{DRIVER_SUPPORT.phoneDisplay}</a>
              </p>
              <p>
                <strong>Email:</strong>{' '}
                <a href={DRIVER_SUPPORT.mailto}>{DRIVER_SUPPORT.email}</a>
              </p>
              <p className="mb-0">
                <strong>Address:</strong> {DRIVER_SUPPORT.address}
              </p>
            </div>

            <div className="driver-legal-actions">
              <Link to={DRIVER_ROUTES.dataDeletion} className="btn driver-btn-outline">
                Account &amp; Data Deletion
              </Link>
              <Link to={DRIVER_ROUTES.support} className="btn driver-btn-primary">
                Driver Support
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DriverTerms;
